import { useEffect, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { motion } from 'framer-motion';
import { toast } from 'sonner';
import { useCart } from '../context/CartContext';
import { useAuth } from '../context/AuthContext';
import { Navbar } from '../components/Navbar';
import { Footer } from '../components/Footer';
import { Button } from '../components/ui/button';
import { Skeleton } from '../components/ui/skeleton';
import {
  AlertDialog,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '../components/ui/alert-dialog';
import { Trash2, ShoppingBag, CreditCard, ShieldCheck, Pencil } from 'lucide-react';
import { formatPrice } from '../utils/format';
import { getErrorMessage } from '../utils/errors';
import { useDocumentTitle } from '../hooks/use-document-title';

// Pill buttons: ink for the main action, thin gradient outline for the rest.
const PILL = 'h-11 rounded-full px-5 text-sm font-bold';
const PILL_INK = `${PILL} bg-ink text-paper hover:bg-ink/90`;
const PILL_OUTLINE = `${PILL} gradient-border text-ink shadow-none hover:[--gb-bg:#F2F0EB]`;

export default function CartPage() {
  useDocumentTitle('Your cart');
  const navigate = useNavigate();
  const { cart, loading, fetchCart, removeFromCart, checkout, cartTotal } = useCart();
  const { user } = useAuth();
  const [checkingOut, setCheckingOut] = useState(false);
  const [removingId, setRemovingId] = useState(null);
  const [showAdminDialog, setShowAdminDialog] = useState(false);
  const [hasLoaded, setHasLoaded] = useState(false);

  useEffect(() => {
    fetchCart().finally(() => setHasLoaded(true));
  }, [fetchCart]);

  const items = cart.items || [];

  const handleRemove = async (itemId) => {
    setRemovingId(itemId);
    try {
      await removeFromCart(itemId);
      toast.success('Removed from cart');
    } catch (error) {
      toast.error(getErrorMessage(error, 'We could not remove that item.'));
    } finally {
      setRemovingId(null);
    }
  };

  /**
   * @param {boolean} skipPayment  admin-only: complete the order without paying
   * @param {boolean} confirmed    admin already chose from the dialog
   */
  const handleCheckout = async (skipPayment = false, confirmed = false) => {
    if (user?.is_admin && !confirmed) {
      setShowAdminDialog(true);
      return;
    }

    setShowAdminDialog(false);
    setCheckingOut(true);
    try {
      const result = await checkout(skipPayment);
      if (result.is_admin || result.skip_payment) {
        await fetchCart();
        navigate(result.redirect_url || '/checkout/success?admin=true');
      } else {
        // Normal purchase → go to the Sola secure card form.
        navigate(result.redirect_url || '/checkout');
      }
    } catch (error) {
      toast.error(getErrorMessage(error, 'We could not start checkout. Please try again.'));
      setCheckingOut(false);
    }
  };

  const showSkeleton = loading && !hasLoaded;

  return (
    <div className="min-h-screen flex flex-col bg-paper">
      <Navbar />

      <main id="main-content" className="flex-1 pt-28 sm:pt-32 pb-20 px-4 sm:px-6">
        <div className="max-w-5xl mx-auto">
          <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }}>
            <p className="text-[13px] font-bold uppercase tracking-[0.16em] text-[#6E5B1C] mb-2">Checkout</p>
            <h1 className="font-serif text-4xl sm:text-5xl font-semibold tracking-tight text-ink mb-2">
              Your <span className="italic font-medium">cart</span>
            </h1>
            <p className="text-[#4A4640] mb-10">Review your digital invitations before checkout.</p>

            {showSkeleton ? (
              <div className="grid lg:grid-cols-3 gap-6 lg:gap-8" aria-busy="true">
                <div className="lg:col-span-2 space-y-4">
                  <Skeleton className="h-36 w-full rounded-[18px] bg-ink/10" />
                  <Skeleton className="h-36 w-full rounded-[18px] bg-ink/10" />
                </div>
                <Skeleton className="h-56 w-full rounded-[24px] bg-ink/10" />
              </div>
            ) : items.length === 0 ? (
              <div className="text-center py-14 px-6 bg-cream rounded-[28px]">
                <div className="w-14 h-14 mx-auto mb-5 rounded-full gradient-border gb-cream flex items-center justify-center">
                  <ShoppingBag className="h-6 w-6 text-ink" aria-hidden="true" />
                </div>
                <h2 className="font-serif text-2xl font-semibold text-ink mb-2">Your cart is empty</h2>
                <p className="text-[#4A4640] mb-7 max-w-md mx-auto">
                  Save an invitation in the editor, then choose “Add to cart” to purchase the watermark-free version.
                </p>
                <div className="flex flex-col sm:flex-row gap-3 justify-center">
                  <Button asChild className={PILL_INK}>
                    <Link to="/templates">Browse designs</Link>
                  </Button>
                  <Button asChild variant="ghost" className={`${PILL_OUTLINE} gb-cream`}>
                    <Link to="/dashboard">My invitations</Link>
                  </Button>
                </div>
              </div>
            ) : (
              <div className="grid lg:grid-cols-3 gap-6 lg:gap-8">
                {/* Cart Items */}
                <ul className="lg:col-span-2 space-y-4" aria-label="Items in your cart">
                  {items.map((item, index) => (
                    <motion.li
                      key={item.id}
                      initial={{ opacity: 0, y: 20 }}
                      animate={{ opacity: 1, y: 0 }}
                      transition={{ delay: Math.min(index, 8) * 0.05 }}
                      className="bg-white border border-[#E6E1D6] rounded-[18px] p-4 sm:p-5"
                    >
                      <div className="flex gap-4 sm:gap-5">
                        {item.template && (
                          <img
                            src={item.template.thumbnail_image || item.template.background_image}
                            alt=""
                            loading="lazy"
                            className="w-20 sm:w-24 aspect-[8.5/11] object-cover rounded-[3px] shrink-0 shadow-[0_8px_16px_-8px_rgba(26,26,26,0.4)]"
                          />
                        )}
                        <div className="flex-1 min-w-0 flex flex-col">
                          <div className="flex items-start justify-between gap-3">
                            <div className="min-w-0">
                              <h2 className="font-bold text-base sm:text-lg text-ink truncate">
                                {item.invitation?.name || 'Invitation'}
                              </h2>
                              {item.template?.name && (
                                <p className="text-[#6B665C] text-sm truncate">{item.template.name}</p>
                              )}
                            </div>
                            <p className="font-serif text-xl font-semibold text-ink shrink-0">
                              {formatPrice(item.price || 0)}
                            </p>
                          </div>
                          <span className="self-start mt-2 inline-flex items-center rounded-full px-2.5 py-1 text-xs font-bold bg-[#EEEBE4] text-[#4A4640]">
                            Digital download
                          </span>
                          <div className="mt-auto pt-3 flex flex-wrap items-center justify-between gap-2">
                            {item.invitation?.id ? (
                              <Link
                                to={`/editor/invitation/${item.invitation.id}`}
                                className="inline-flex items-center gap-1.5 text-sm font-semibold text-ink hover:underline underline-offset-4"
                              >
                                <Pencil className="h-3.5 w-3.5" aria-hidden="true" />
                                Edit design
                              </Link>
                            ) : (
                              <span />
                            )}
                            <Button
                              variant="ghost"
                              size="sm"
                              className="h-9 rounded-full px-3 text-[#6B665C] hover:text-destructive hover:bg-destructive/10"
                              onClick={() => handleRemove(item.id)}
                              disabled={removingId === item.id || checkingOut}
                              aria-label={`Remove ${item.invitation?.name || 'item'} from cart`}
                              data-testid={`remove-item-${item.id}`}
                            >
                              <Trash2 className="h-4 w-4" />
                              <span className="hidden sm:inline">
                                {removingId === item.id ? 'Removing…' : 'Remove'}
                              </span>
                            </Button>
                          </div>
                        </div>
                      </div>
                    </motion.li>
                  ))}
                </ul>

                {/* Order Summary */}
                <div className="lg:sticky lg:top-28 self-start w-full">
                  <div className="gradient-border gb-cream rounded-[24px] p-6 flex flex-col gap-4">
                    <h2 className="font-serif text-2xl font-semibold text-ink">Order summary</h2>
                    <div className="flex justify-between text-[15px]">
                      <span className="text-[#4A4640]">
                        Subtotal ({items.length} item{items.length === 1 ? '' : 's'})
                      </span>
                      <span className="font-semibold">{formatPrice(cartTotal)}</span>
                    </div>
                    <div className="border-t border-[#DCD6C8] pt-4 flex justify-between items-baseline">
                      <span className="font-bold">Total</span>
                      <span className="font-serif text-3xl font-semibold text-ink">{formatPrice(cartTotal)}</span>
                    </div>

                    <Button
                      onClick={() => handleCheckout(false)}
                      disabled={checkingOut}
                      className={`${PILL_INK} w-full h-12 text-[15px]`}
                      data-testid="checkout-btn"
                    >
                      <CreditCard className="h-5 w-5" />
                      {checkingOut
                        ? 'Starting checkout…'
                        : user?.is_admin
                        ? 'Proceed to Checkout'
                        : `Checkout ${formatPrice(cartTotal)}`}
                    </Button>
                    <p className="text-xs text-[#6B665C] flex items-center gap-1.5 justify-center text-center">
                      <ShieldCheck className="h-3.5 w-3.5 shrink-0" aria-hidden="true" />
                      Secure card payment processed by Sola Payments
                    </p>
                  </div>
                </div>
              </div>
            )}
          </motion.div>
        </div>
      </main>

      <Footer />

      {/* Admin checkout choice */}
      <AlertDialog open={showAdminDialog} onOpenChange={setShowAdminDialog}>
        <AlertDialogContent className="rounded-[24px]">
          <AlertDialogHeader>
            <AlertDialogTitle className="font-serif text-2xl font-semibold">Admin checkout</AlertDialogTitle>
            <AlertDialogDescription>
              As an admin you can complete this order without payment, or pay {formatPrice(cartTotal)} by card as a
              customer would.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter className="gap-2">
            <AlertDialogCancel className="rounded-full">Cancel</AlertDialogCancel>
            <Button variant="ghost" onClick={() => handleCheckout(true, true)} className={PILL_OUTLINE}>
              Skip payment
            </Button>
            <Button onClick={() => handleCheckout(false, true)} className={PILL_INK}>
              Pay {formatPrice(cartTotal)}
            </Button>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}
